import {
  AUTH_STORAGE_KEYS,
  type AuthResponse,
  type LoginCredentials,
  type User,
} from '@features/authentication/types';
import { API_ROUTES } from '@shared/constants/api.constants';
import { apiClient, handleAPIError } from '@shared/utils/api/client';

/**
 * Helper to store auth tokens in localStorage
 */
const storeTokens = (response: AuthResponse): void => {
  localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
  localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);

  // Calculate expiration timestamp
  const expiresAt = Date.now() + response.expiresIn * 1000;
  localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
};

/**
 * Helper to clear auth tokens from localStorage
 */
const clearTokens = (): void => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
};

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
export const authService = {
  /**
   * Login with email and password
   * @param credentials - Login credentials (email, password)
   * @returns Promise with authentication response
   */
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>(API_ROUTES.AUTH.LOGIN, credentials);

      // Store tokens in localStorage
      storeTokens(response.data);

      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post(API_ROUTES.AUTH.LOGOUT);
    } catch (error) {
      // Log error but don't throw - allow logout to complete
      console.error('Logout error:', error);
    } finally {
      // Always remove tokens from localStorage
      clearTokens();
    }
  },

  /**
   * Get current authenticated user
   * @returns Promise with user information
   */
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await apiClient.get<User>(API_ROUTES.AUTH.ME);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Refresh authentication token
   * @returns Promise with new authentication response
   */
  refreshToken: async (): Promise<AuthResponse> => {
    try {
      const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);

      const response = await apiClient.post<AuthResponse>(API_ROUTES.AUTH.REFRESH, {
        refreshToken,
      });

      // Update tokens in localStorage
      storeTokens(response.data);

      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Check if user is authenticated
   * @returns true if user has valid token
   */
  isAuthenticated: (): boolean => {
    const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    const expiresAt = localStorage.getItem(AUTH_STORAGE_KEYS.EXPIRES_AT);

    if (!accessToken || !expiresAt) {
      return false;
    }

    // Check if token is expired
    return Date.now() < parseInt(expiresAt, 10);
  },

  /**
   * Get stored access token
   * @returns Token string or null
   */
  getAccessToken: (): string | null => {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get stored refresh token
   * @returns Refresh token string or null
   */
  getRefreshToken: (): string | null => {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  },

  /**
   * Generate OTP for password recovery
   * @param email - User email address
   * @returns Promise that resolves on success (201)
   * @throws APIError on failure (404 if user not found)
   */
  generateOtp: async (email: string): Promise<void> => {
    try {
      await apiClient.post(API_ROUTES.AUTH.GENERATE_OTP(email));
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Verify OTP code for password recovery
   * @param email - User email address
   * @param otp - 6-digit OTP code
   * @returns Promise that resolves on success (200)
   * @throws APIError on failure (400 if invalid OTP)
   */
  verifyOtp: async (email: string, otp: string): Promise<void> => {
    try {
      await apiClient.get(API_ROUTES.AUTH.VERIFY_OTP(email, otp));
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Update password after OTP verification
   * @param email - User email address
   * @param otp - 6-digit OTP code
   * @param newPassword - New password
   * @returns Promise that resolves on success (204)
   * @throws APIError on failure (404 if user not found)
   */
  updatePassword: async (email: string, otp: string, newPassword: string): Promise<void> => {
    try {
      await apiClient.patch(API_ROUTES.AUTH.UPDATE_PASSWORD, {
        email,
        otp,
        newPassword,
      });
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Set password for first access users
   * @param newPassword - New password to set
   * @returns Promise that resolves on success (204)
   * @throws APIError on failure (400 if password already set)
   */
  setPassword: async (newPassword: string): Promise<void> => {
    try {
      await apiClient.patch(API_ROUTES.AUTH.SET_PASSWORD, {
        newPassword,
      });
    } catch (error) {
      throw handleAPIError(error);
    }
  },
};
